import React from 'react';

import { usePatchGroupStreams } from '@vlabs/api/artemy/client';
import {
  PlayCircleIcon, StopCircleIcon,
} from '@vlabs/icons';
import { useHasPermission } from '@vlabs/permissions';
import { ButtonScrimDivider, ButtonScrimGroup } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useStartStopGroupStreamsTranslation } from '../i18n';
import type { StartStopGroupStreamsProps } from '../types';

function StartStopGroupStreamsButtons({
  groupId,
  ...props
}: StartStopGroupStreamsProps & ButtonProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { t: tFeature } = useStartStopGroupStreamsTranslation();
  const canUpdateGroup = useHasPermission('group', 'update');
  const patchGroupStreams = usePatchGroupStreams({
    mutation: {
      onSuccess: () => toast.success(tFeature('on_success')),
      onError: () => toast.error(tFeature('on_error')),
    },
  });

  if (!canUpdateGroup) return null;

  const patch = (action: 'stop' | 'resume') => {
    patchGroupStreams.mutate({ groupId, params: { action } });
  };

  return (
    <ButtonScrimGroup
      orientation="horizontal"
      shape="square"
      size="small"
    >
      <IconButton
        icon={<PlayCircleIcon />}
        kind="secondary"
        onClick={() => patch('resume')}
        title={t('entities:source_filters.start_all')}
        variant="flat"
        {...props}
      >
        {t('entities:source_filters.start_all')}
      </IconButton>
      <ButtonScrimDivider />
      <IconButton
        icon={<StopCircleIcon />}
        kind="secondary"
        onClick={() => patch('stop')}
        title={t('entities:source_filters.stop_all')}
        variant="flat"
        {...props}
      >
        {t('entities:source_filters.stop_all')}
      </IconButton>
    </ButtonScrimGroup>
  );
}

export { StartStopGroupStreamsButtons };
